import { Toaster as Sonner } from "sonner";
import { useTheme } from "@/context/ThemeContext";

/**
 * Theme-matched toast wrapper.
 *
 * Visual rules:
 *  - Surface matches the notification bell popover (bg-popover, border-border,
 *    rounded-md, shadow-xl).
 *  - Variant is signalled by a 3px left accent strip + matching icon color,
 *    not by recoloring the whole surface (avoids the loud Sonner "richColors"
 *    aesthetic that fights our minimalist palette).
 *  - Action button uses our primary tokens; close button is neutral.
 */
const baseToast =
  "group toast " +
  "group-[.toaster]:bg-popover group-[.toaster]:text-foreground " +
  "group-[.toaster]:border group-[.toaster]:border-border " +
  "group-[.toaster]:border-l-[3px] " +
  "group-[.toaster]:shadow-xl group-[.toaster]:rounded-md " +
  "group-[.toaster]:p-4";

// Variant tinting — only the LEFT strip and the variant icon change.
const variantStrips = {
  default: "group-[.toaster]:border-l-border",
  info:    "group-[.toaster]:border-l-primary",
  success: "group-[.toaster]:border-l-accent",
  warning: "group-[.toaster]:border-l-chart-5",
  error:   "group-[.toaster]:border-l-destructive",
};

const variantIcons = {
  info:    "group-[.toast]:text-primary",
  success: "group-[.toast]:text-accent",
  warning: "group-[.toast]:text-chart-5",
  error:   "group-[.toast]:text-destructive",
};

export function Toaster(props) {
  const { theme } = useTheme();
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position="top-right"
      closeButton
      toastOptions={{
        classNames: {
          toast: `${baseToast} ${variantStrips.default}`,
          default: variantStrips.default,
          info:    variantStrips.info,
          success: variantStrips.success,
          warning: variantStrips.warning,
          error:   variantStrips.error,
          title: "text-sm font-medium",
          description: "group-[.toast]:text-muted-foreground text-xs mt-0.5",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground " +
            "group-[.toast]:rounded-md group-[.toast]:px-2.5 group-[.toast]:py-1 " +
            "group-[.toast]:text-xs group-[.toast]:font-medium " +
            "group-[.toast]:hover:opacity-90 group-[.toast]:transition",
          cancelButton:
            "group-[.toast]:bg-secondary group-[.toast]:text-muted-foreground " +
            "group-[.toast]:rounded-md group-[.toast]:px-2.5 group-[.toast]:py-1 " +
            "group-[.toast]:text-xs",
          closeButton:
            "group-[.toast]:bg-popover group-[.toast]:border " +
            "group-[.toast]:border-border group-[.toast]:text-muted-foreground " +
            "group-[.toast]:hover:text-foreground group-[.toast]:hover:bg-secondary",
          icon: variantIcons.info, // default; per-variant overrides below
          loader: "group-[.toast]:text-primary",
        },
      }}
      {...props}
    />
  );
}

export { toast } from "sonner";
